import HealthMonitor from '@omni/kit/HealthMonitor';
import {
  KitBadge,
  KitButton,
  KitIcon,
  KitInput,
  KitListItem,
  KitLoader,
  KitSnack,
  KitText,
  KitTouchable,
} from '@omni/kit/components';
import KitAvatar from '@omni/kit/components/KitAvatar';
import KitCheckmark from '@omni/kit/components/KitCheckmark';
import KitCounterInput from '@omni/kit/components/KitCounterInput';
import KitList from '@omni/kit/components/KitList';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import GroupsService from '@omni/kit/services/GroupsService';
import { IAttendanceRequest } from '@omni/kit/services/GroupsService/Core/CreateGroupEventAttendances';
import {
  IAttendance,
  IGroupAttendances,
  IGroupEvent,
  IGroupHeadCounts,
  IGroupMember,
} from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import { createImageUrl } from '@omni/kit/utilities/utilities';
import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, ScrollViewProps, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import LinearGradient from 'react-native-linear-gradient';
import { useSelector } from 'react-redux';

import PlatformKeyboardSpacer from '../../../../mobile/components/PlatformKeyboardSpacer';
import { sortMembers } from '../../../../utilities/sortGroupMembers';
import { ownerListSelector, userSelector } from '../../../redux/selectors';

export type AttendanceMode = 'create' | 'edit';
const debug = require('debug')(
  'tca:chat:screen:CreateOrUpdateGroupAttendanceScreen'
);
interface Props {
  route: any;
  mode: AttendanceMode;
  onCancel?: (open?: boolean) => void;
  onSuccess?: () => void;
  headcounts?: IGroupHeadCounts | null;
  attendances?: IGroupAttendances | null;
  groupEvent?: IGroupEvent | undefined;
  takeAttendanceQuery?: boolean;
}
const CreateOrUpdateGroupAttendance: React.FC<Props> = ({
  route,
  mode,
  onCancel,
  onSuccess,
  headcounts,
  attendances,
  groupEvent,
  takeAttendanceQuery,
}) => {
  const { tokens, app } = useShellContext();
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }
  const appKey = app.appKey;
  const accessToken = useMemo(() => tokens.user, [tokens.user]);
  const currentUser = useSelector(userSelector);
  const currentId = currentUser?.userId;

  const ownerList = useSelector(ownerListSelector);
  const [guestCountManuallyEdited, setGuestCountManuallyEdited] =
    useState(false);
  const { windowHeight, windowWidth } = useSizeClass();
  const isLandscape = windowHeight < windowWidth;
  const { sizeClass } = useSizeClass();
  const { t } = useTranslation();
  const [members, setMembers] = useState<IGroupMember[]>([]);
  const sortedMembers = useMemo(() => sortMembers(members), [members]);
  const initialNote =
    groupEvent?._embedded?.sessions?.[0]?.note ||
    route?.params?.groupEvent?._embedded?.sessions?.[0]?.note ||
    '';

  const [newUsers, setNewUsers] = useState<IGroupMember[]>([]);
  const [checkedMembers, setCheckedMembers] = useState<{
    [userId: string]: boolean;
  }>({});
  const checkedMembersRef = useRef(checkedMembers);
  const [selectAllChecked, setSelectAllChecked] = useState(
    members?.length > 0 ? true : false
  );
  const [initialCheckedMembers, setInitialCheckedMembers] = useState<{
    [key: string]: boolean;
  }>({});
  const groupEventAttendances = attendances?._embedded?.attendances
    ? attendances?._embedded?.attendances
    : route.params?.groupEventAttendances?._embedded?.attendances;
  const attendanceMode: AttendanceMode = mode ? mode : route.params.mode;
  const onSuccessCallback = onSuccess ?? route.params.onSuccess;
  const groupEventDetails: IGroupEvent = groupEvent
    ? groupEvent
    : route?.params?.groupEvent;
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(true);
  const isSmall = sizeClass === SizeClass.Small;
  const isWeb = Platform.OS === 'web';
  const isCreateMode = attendanceMode === 'create';
  const isEditMode = attendanceMode === 'edit';
  const [initialHeadCount, setInitialHeadCount] = useState<number>(0);
  const [totalHeadCount, setTotalHeadCount] = useState(0);
  const [guestCount, setGuestCount] = useState(initialHeadCount);
  const isManager = (member: IGroupMember): boolean => {
    return ownerList.some(
      (m) =>
        m._embedded?.['chat-user']?._embedded?.['end-user']?.id ===
        member._embedded['end-user']?.id
    );
  };
  const [isLoadingGroupMembers, setIsLoadingGroupMembers] = useState(true);
  const [totalGroupMembers, setTotalGroupMembers] = useState(0);
  const [note, setNote] = useState<string>(initialNote);

  const groupMembersLessThanFifty = members && members?.length < 50;
  const hasHeadcountChanged = guestCount !== initialHeadCount;

  const hasCheckedMembersChanged = useMemo(() => {
    const keysA = Object.keys(checkedMembers);
    const keysB = Object.keys(initialCheckedMembers);

    if (keysA.length !== keysB.length) return true;

    for (const key of keysA) {
      if (checkedMembers[key] !== initialCheckedMembers[key]) {
        return true;
      }
    }

    return false;
  }, [checkedMembers, initialCheckedMembers]);

  const hasNoteChanged = useMemo(() => {
    return note !== initialNote;
  }, [note, initialNote]);

  const toggleCheckIn = (userId: string) => {
    setCheckedMembers((prev) => {
      const newChecked = { ...prev, [userId]: !prev[userId] };

      const allMemberIds = Object.keys(newChecked);

      const allChecked =
        allMemberIds.length > 0 && allMemberIds.every((id) => newChecked[id]);
      setSelectAllChecked(allChecked);

      return newChecked;
    });
  };

  const toggleSelectAll = () => {
    const newValue = !selectAllChecked;

    const updated: { [key: string]: boolean } = {};
    const targetMembers =
      attendanceMode === 'edit'
        ? sortedMembers.filter((m) => {
            const endUserId = m._embedded?.['end-user']?.id;

            return groupEventAttendances?.some(
              (a: IAttendance) => a._embedded?.['end-user']?.id === endUserId
            );
          })
        : sortedMembers;

    for (const member of targetMembers) {
      updated[member._embedded['end-user']?.id ?? ''] = newValue;
    }

    setCheckedMembers((prev) => ({ ...prev, ...updated }));

    setSelectAllChecked(newValue);
  };
  const handleGuestCountChange = (value: number) => {
    setGuestCount(value);
    setGuestCountManuallyEdited(true);
  };

  const handleCancel = () => {
    if (isLandscape && Platform.OS !== 'web') {
      navigation.goBack();
    } else if (Platform.OS === 'web') {
      onCancel?.(false);
    }
  };

  const handleSave = useCallback(async () => {
    try {
      const eventId = groupEventDetails?.id;
      const repeatingEventId =
        groupEventDetails?._embedded?.['repeating-event']?.id;
      const sessionId =
        route?.params?.sessionId ||
        groupEventDetails?._embedded?.sessions?.[0]?.id;

      const groupId = groupEventDetails?._embedded?.group?.id;
      const calendarId = groupEventDetails?._embedded?.calendar?.id;

      if (!accessToken || !sessionId || !eventId) {
        console.warn('Missing required data');

        return;
      }

      const embedded = {
        event: { id: eventId },
        ...(sessionId && { session: { id: sessionId } }),
        ...(repeatingEventId && {
          'repeating-event': { id: repeatingEventId },
        }),
        ...(groupEventDetails?._embedded?.location && {
          location: groupEventDetails._embedded.location,
        }),
      };

      // Save headcount (only in create/edit mode)
      let headcountResponse;

      if (attendanceMode === 'create') {
        headcountResponse = await GroupsService.CreateGroupEventHeadCounts({
          token: accessToken,
          app_key: appKey,
          count: groupMembersLessThanFifty ? guestCount : totalHeadCount,
          _embedded: embedded,
        });
      } else if (attendanceMode === 'edit') {
        const headcountId = headcounts?._embedded?.headcounts?.[0]?.id ?? '';
        headcountResponse = await GroupsService.UpdateGroupEventsHeadCounts({
          id: headcountId,
          token: accessToken,
          app_key: appKey,
          count: groupMembersLessThanFifty ? guestCount : totalHeadCount,
          _embedded: embedded,
        });
      }

      const attendanceRequests: IAttendanceRequest[] = sortedMembers.map(
        (member) => {
          const isChecked =
            checkedMembers[member._embedded['end-user']?.id ?? ''] ?? false;

          return {
            app_key: appKey,
            attended: isChecked,
            _embedded: {
              ['end-user']: {
                id: member._embedded['end-user']?.id ?? '',
              },
              event: { id: eventId },
              session: { id: sessionId },
              group: { id: groupId ?? '' },
            },
          };
        }
      );
      let attendanceResponse;

      if (groupMembersLessThanFifty) {
        attendanceResponse = await GroupsService.CreateGroupEventAttendances({
          token: accessToken,
          attendances: attendanceRequests,
        });
      }

      // Update session note if it has changed (only in edit mode)
      let noteUpdateResponse;

      if (hasNoteChanged && eventId && groupId && calendarId) {
        const currentSessions = groupEventDetails?._embedded?.sessions;

        if (currentSessions && currentSessions.length > 0) {
          // Update the sessions array by mapping over it and updating the note for the matching session
          const updatedSessions = currentSessions.map((session) => {
            if (session.id === sessionId) {
              return {
                ...session,
                note: note,
              };
            }

            return session;
          });

          noteUpdateResponse = await GroupsService.UpdateGroupEvent({
            id: eventId,
            token: accessToken,
            body: {
              ...groupEventDetails,
              _embedded: {
                ...groupEventDetails._embedded,
                sessions: updatedSessions,
              },
            },
          });

          // Check for errors in the response (server errors are not thrown as exceptions)
          if (
            noteUpdateResponse &&
            !noteUpdateResponse.ok &&
            noteUpdateResponse.status
          ) {
            if (!excludedStatusCodes.includes(noteUpdateResponse.status)) {
              HealthMonitor.logError(
                new Error(
                  `Unable to save group attendance: ${
                    noteUpdateResponse.message || 'Unknown error'
                  }`
                )
              );
            }
          }
        }
      }

      if (
        (headcountResponse?.status === 200 &&
          (!groupMembersLessThanFifty || attendanceResponse?.status === 201)) ||
        (headcountResponse?.status === 201 &&
          (!groupMembersLessThanFifty || attendanceResponse?.status === 201))
      ) {
        KitSnack.show(
          attendanceMode === 'create'
            ? t('groups:attendanceSubmitted')
            : t('groups:attendanceSaved'),
          KitSnackDuration.SHORT
        );
        onSuccessCallback?.();

        if (Platform.OS === 'web') {
          onCancel?.(false);
        } else {
          navigation.goBack();
        }
      } else {
        KitSnack.show(t('groups:errorMessage'), KitSnackDuration.SHORT);
      }
    } catch (error) {
      console.error('Error during save operation', error);
    }
  }, [
    groupEventDetails,
    attendanceMode,
    accessToken,
    appKey,
    headcounts,
    guestCount,
    sortedMembers,
    checkedMembers,
    navigation,
    route?.params?.sessionId,
    onCancel,
    onSuccessCallback,
    t,
    groupMembersLessThanFifty,
    hasNoteChanged,
    note,
    totalHeadCount,
  ]);

  const onChangeNote = (text: string) => {
    setNote(text);
  };
  const fetchGroupMembers = useCallback(() => {
    const _fetchGroupMembers = async () => {
      setIsLoadingGroupMembers(true);
      try {
        const response = await GroupsService.GetGroupMembers({
          token: accessToken,
          filters: [['group.id', groupEvent?._embedded?.group?.id ?? '']],
          pages: [
            ['size', 51],
            ['number', 1],
          ],
        });

        if (response.body && response.body._embedded?.['group-members']) {
          setMembers(response.body._embedded['group-members']);

          setTotalGroupMembers(response.body?.total);
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoadingGroupMembers(false);
      }
    };
    _fetchGroupMembers();
  }, [accessToken, groupEvent]);

  useEffect(() => {
    const groupId = groupEvent?._embedded?.group?.id;

    if (accessToken && groupId) {
      fetchGroupMembers();
    }
  }, [fetchGroupMembers, accessToken, groupEvent]);
  useEffect(() => {
    setNote(initialNote);
  }, [initialNote]);

  const handleScroll = (event: {
    nativeEvent: {
      contentOffset: any;
      layoutMeasurement: any;
      contentSize: any;
    };
  }) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;

    const isAtTop = contentOffset.y <= 0;
    const isAtBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height;

    // Show the top shadow when scrolling starts and hide it only at the very top
    setShowTopShadow(!isAtTop);

    // Show the bottom shadow always unless at the very end
    setShowBottomShadow(!isAtBottom);
  };
  useEffect(() => {
    checkedMembersRef.current = checkedMembers;
  }, [checkedMembers]);

  useEffect(() => {
    const checkedCount = Object.values(checkedMembers).filter(Boolean).length;

    if (attendanceMode === 'edit') {
      setTotalHeadCount(checkedCount + guestCount);
    } else if (attendanceMode === 'create') {
      if (groupMembersLessThanFifty) {
        setTotalHeadCount(checkedCount + guestCount);
      } else {
        setTotalHeadCount(totalGroupMembers + guestCount);
      }
    }
  }, [
    checkedMembers,
    totalGroupMembers,
    guestCount,
    attendanceMode,
    groupMembersLessThanFifty,
    members,
  ]);

  useEffect(() => {
    if (Platform.OS !== 'web' && !isLandscape) {
      navigation.setOptions({
        headerRight: () => (
          <KitTouchable
            onPress={handleSave}
            disabled={
              attendanceMode === 'create' ||
              hasHeadcountChanged ||
              hasNoteChanged
                ? false
                : !hasCheckedMembersChanged
            }
            style={{
              paddingLeft: 12,
              paddingRight: Platform.OS === 'ios' ? 12 : 18,
              paddingVertical: 6,
              marginRight: -6,
              borderRadius: 40,
            }}
          >
            <KitText
              color={
                attendanceMode === 'create' ||
                hasHeadcountChanged ||
                hasCheckedMembersChanged ||
                hasNoteChanged
                  ? Colors.brand
                  : Colors.N400
              }
              fontSize={17}
            >
              {route?.params?.mode === 'edit'
                ? t('common:buttonTitleSave')
                : t('common:submitButtonTitle')}
            </KitText>
          </KitTouchable>
        ),
      });
    }
  }, [
    navigation,
    isSmall,
    route?.params?.mode,
    t,
    handleSave,
    isLandscape,
    hasHeadcountChanged,
    attendanceMode,
    hasCheckedMembersChanged,
    hasNoteChanged,
  ]);
  const sessionId =
    route?.params?.sessionId || groupEventDetails?._embedded?.sessions?.[0]?.id;

  useEffect(() => {
    if (!members?.length) return;

    const newCheckedMembers: { [key: string]: boolean } = {};
    const newUsers: IGroupMember[] = [];
    const attendedIds = new Set<string>();

    if (attendanceMode === 'edit' && groupEventAttendances?.length) {
      for (const member of members) {
        const endUserId = member._embedded?.['end-user']?.id;
        const attendanceRecord = groupEventAttendances.find(
          (a: IAttendance) => a._embedded?.['end-user']?.id === endUserId
        );

        if (attendanceRecord) {
          newCheckedMembers[member._embedded?.['end-user']?.id ?? ''] =
            attendanceRecord.attended === true;

          if (attendanceRecord.attended === true) {
            attendedIds.add(member._embedded?.['end-user']?.id ?? '');
          }
        } else {
          newUsers.push(member);
          newCheckedMembers[member._embedded?.['end-user']?.id ?? ''] = false;
        }
      }
    } else if (attendanceMode === 'create') {
      for (const member of members) {
        newCheckedMembers[member._embedded?.['end-user']?.id ?? ''] = true;
      }
    }

    setCheckedMembers(newCheckedMembers);
    setInitialCheckedMembers(newCheckedMembers);
    setNewUsers(newUsers);

    const allChecked = Object.values(newCheckedMembers).every((val) => val);
    setSelectAllChecked(allChecked);
  }, [attendanceMode, members, groupEventAttendances, initialHeadCount]);

  useEffect(() => {
    if (!headcounts?._embedded?.headcounts?.length) return;

    const originalCount = headcounts._embedded.headcounts[0].count ?? 0;
    setInitialHeadCount(originalCount);

    if (!guestCountManuallyEdited) {
      setGuestCount(originalCount);
    }
  }, [headcounts, guestCountManuallyEdited]);

  return (
    <>
      {Platform.OS === 'web' ? (
        <View
          style={[
            {
              padding: Spacing.l,
              paddingBottom: Spacing.l,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
            showTopShadow && {
              backgroundColor: Colors.N0,
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowOffset: { width: 0, height: 3 },
              shadowOpacity: 1,
              shadowRadius: 10,
              zIndex: 999,
            },
            sizeClass === SizeClass.Small && {
              alignItems: 'center',
            },
          ]}
        >
          <KitText
            black
            bold
            fontSize={20}
            testID={
              attendanceMode === 'edit'
                ? 'editAttendanceButton'
                : 'takeAttendanceButton'
            }
          >
            {attendanceMode === 'edit'
              ? t('groups:editAttendance')
              : t('groups:takeAttendanceButton')}
          </KitText>
        </View>
      ) : null}
      <ScrollViewWrapper
        onScroll={handleScroll}
        keyboardShouldPersistTaps='handled'
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        setScrollable={(isScrollable) => setShowBottomShadow(isScrollable)}
      >
        <View style={{ flex: 1, padding: Spacing.l }}>
          {/* only in create mode and less than 50 members */}
          {!(attendanceMode === 'create' && !groupMembersLessThanFifty) ? (
            <View style={{ marginBottom: Spacing.xl }}>
              <KitText
                black
                bold
                fontSize={20}
                testID='guests'
                style={{ marginBottom: Spacing.s }}
              >
                {t('groups:guests')}
              </KitText>

              <KitListItem
                testID='guestCountInput'
                ImageComponent={
                  <KitIcon
                    name='eye'
                    color={Colors.N600}
                    size={20}
                    style={{ margin: Spacing.s }}
                  />
                }
                rightElement={
                  <View>
                    <KitCounterInput
                      width={60}
                      value={guestCount}
                      onChange={handleGuestCountChange}
                    />
                  </View>
                }
                title={t('groups:countsGuests', { count: guestCount })}
                titleFontSize={16}
                titleLineHeight={22}
                isTitleBold={false}
                topBorder
                bottomBorder={false}
                iconColor={Colors.N300}
                minHeight={78}
              />
            </View>
          ) : null}

          {/* Member List — in edit mode, or create mode if less than 50 */}
          {(attendanceMode === 'edit' && groupEventAttendances?.length > 0) ||
          (attendanceMode === 'create' && groupMembersLessThanFifty) ? (
            <>
              <View
                style={{
                  paddingRight: Spacing.s,
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <KitText black bold fontSize={20} testID='members'>
                  {t('groups:members')}
                </KitText>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: Spacing.s,
                  }}
                >
                  <KitText fontSize={14} lineHeight={20}>
                    {t('groups:selectAll')}
                  </KitText>
                  <KitCheckmark
                    checked={selectAllChecked}
                    onToggle={toggleSelectAll}
                  />
                </View>
              </View>
              {isLoadingGroupMembers ? (
                <View
                  style={{
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: Colors.N0,
                  }}
                >
                  <KitLoader />
                </View>
              ) : (
                <KitList
                  animationEnabled={false}
                  data={
                    attendanceMode === 'edit'
                      ? sortedMembers.filter((m) => {
                          const endUserId = m._embedded?.['end-user']?.id;

                          return groupEventAttendances?.some(
                            (a: IAttendance) =>
                              a._embedded?.['end-user']?.id === endUserId
                          );
                        })
                      : sortedMembers
                  }
                  renderItem={({ item, index }) => {
                    const nickname = `${
                      item._embedded['end-user']?.first_name
                    } ${item._embedded['end-user']?.last_name}${
                      item._embedded?.['end-user']?.id === currentId
                        ? ' (you)'
                        : ''
                    }`;

                    const photoId =
                      item?._embedded?.['end-user']?._embedded?.profiles?.[0]
                        ?._embedded?.photo?.id ?? '';
                    const imagePhotoUrl = createImageUrl(photoId);

                    const memberIsManager = isManager(item);

                    return (
                      <KitListItem
                        ImageComponent={
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                          >
                            <KitAvatar
                              style={{ marginLeft: Spacing.xs }}
                              nickname={nickname}
                              size={Spacing.xxl}
                              imageUrl={imagePhotoUrl}
                            />
                          </View>
                        }
                        minHeight={76}
                        title={nickname}
                        titleFontSize={16}
                        rightElement={
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            {memberIsManager ? (
                              <KitBadge
                                text={t('groups:labelGroupManager')}
                                size='l'
                              />
                            ) : null}
                            <KitCheckmark
                              checked={Boolean(
                                checkedMembers[
                                  item._embedded['end-user']?.id ?? ''
                                ]
                              )}
                              onToggle={() =>
                                toggleCheckIn(
                                  item._embedded['end-user']?.id ?? ''
                                )
                              }
                            />
                          </View>
                        }
                        rightIcon=''
                        topBorder={index === 0}
                      />
                    );
                  }}
                />
              )}
            </>
          ) : null}
          {attendanceMode === 'edit' && newUsers.length > 0 ? (
            <View style={{ marginTop: Spacing.xl }}>
              <KitText black bold fontSize={20} testID='newMembers'>
                {t('groups:newMembersTitle')}
              </KitText>
              <KitText
                fontSize={14}
                testID='newMembersSubtitle'
                style={{ marginTop: Spacing.xs, marginBottom: Spacing.m }}
              >
                {t('groups:newMembersSubtitle')}
              </KitText>
              <KitList
                animationEnabled={false}
                data={newUsers}
                renderItem={({ item, index }) => {
                  const nickname = `${item._embedded['end-user']?.first_name} ${
                    item._embedded['end-user']?.last_name
                  }${
                    item._embedded?.['end-user']?.id === currentId
                      ? ' (you)'
                      : ''
                  }`;
                  const photoId =
                    item?._embedded?.['end-user']?._embedded?.profiles?.[0]
                      ?._embedded?.photo?.id ?? '';
                  const imagePhotoUrl = createImageUrl(photoId);

                  const memberIsManager = isManager(item);

                  return (
                    <KitListItem
                      ImageComponent={
                        <View
                          style={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                          <KitAvatar
                            style={{ marginLeft: Spacing.xs }}
                            nickname={nickname}
                            size={Spacing.xxl}
                            imageUrl={imagePhotoUrl}
                          />
                        </View>
                      }
                      minHeight={76}
                      title={nickname}
                      titleFontSize={16}
                      rightElement={
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          {memberIsManager ? (
                            <KitBadge
                              text={t('groups:labelGroupManager')}
                              size='l'
                            />
                          ) : null}
                          <KitCheckmark
                            checked={Boolean(
                              checkedMembers[
                                item._embedded['end-user']?.id ?? ''
                              ]
                            )}
                            onToggle={() =>
                              toggleCheckIn(
                                item._embedded['end-user']?.id ?? ''
                              )
                            }
                          />
                        </View>
                      }
                      rightIcon=''
                      topBorder={index === 0}
                    />
                  );
                }}
              />
            </View>
          ) : null}

          {!(attendanceMode === 'create' && !groupMembersLessThanFifty) ? (
            <>
              <KitText
                fontSize={14}
                color={Colors.N600}
                fontWeight={600}
                style={{ marginTop: Spacing.xl, marginBottom: Spacing.s }}
              >
                {t('groups:groupAttendanceNoteLabel')}
              </KitText>
              <KitInput
                autoCapitalize='sentences'
                style={{ maxHeight: 83 }}
                inputStyle={{ paddingRight: 50 }}
                placeholder={t('groups:groupAttendanceNote')}
                onChangeText={onChangeNote}
                value={note}
                multiline
                testID='group-attendance-note-input'
                maxLength={500}
                rightIcon={<KitText fontSize={15}>{500 - note.length}</KitText>}
                rightIconStyle={{
                  bottom: 0,
                  height: 22,
                  right: 14,
                  width: 29,
                }}
              />
            </>
          ) : null}

          {/* Fallback headcount — only in create mode and more than 50 members */}
          {attendanceMode === 'create' && !groupMembersLessThanFifty ? (
            <>
              <View style={{ marginBottom: Spacing.xl }}>
                <KitText black bold fontSize={20} testID='totalHeadCount'>
                  {t('groups:totalHeadCountTitle')}
                </KitText>

                <View
                  style={{
                    borderTopWidth: 1,
                    borderColor: Colors.N100,
                    marginTop: Spacing.s,
                    paddingTop: Spacing.l,
                  }}
                >
                  <KitText fontSize={14} lineHeight={20}>
                    {t('groups:moreThanFiftyGroupAttendancesInfo')}
                  </KitText>
                </View>
              </View>

              <View testID='totalHeadcountSection'>
                <KitCounterInput
                  width={60}
                  value={totalHeadCount}
                  onChange={(val) => setTotalHeadCount(val)}
                />
              </View>

              <KitText
                fontSize={14}
                color={Colors.N600}
                fontWeight={600}
                style={{ marginTop: Spacing.xl, marginBottom: Spacing.s }}
              >
                {t('groups:groupAttendanceNoteLabel')}
              </KitText>
              <KitInput
                autoCapitalize='sentences'
                style={{ maxHeight: 83 }}
                inputStyle={{ paddingRight: 50 }}
                placeholder={t('groups:groupAttendanceNote')}
                onChangeText={onChangeNote}
                value={note}
                multiline
                testID='group-attendance-note-input'
                maxLength={500}
                rightIcon={<KitText fontSize={15}>{500 - note.length}</KitText>}
                rightIconStyle={{
                  bottom: 0,
                  height: 22,
                  right: 14,
                  width: 29,
                }}
              />
            </>
          ) : null}
        </View>
      </ScrollViewWrapper>

      <View
        style={{
          flexDirection:
            Platform.OS === 'web' && sizeClass !== SizeClass.Small
              ? 'row'
              : sizeClass === SizeClass.Small
              ? 'column'
              : 'row',
          alignItems:
            Platform.OS === 'web' && sizeClass !== SizeClass.Small
              ? 'center'
              : sizeClass === SizeClass.Small
              ? 'flex-start'
              : 'center',
          justifyContent:
            isWeb && !isSmall && isCreateMode && !groupMembersLessThanFifty
              ? 'flex-end'
              : (isWeb && !isSmall && isEditMode) ||
                (isWeb && !isSmall && groupMembersLessThanFifty)
              ? 'space-between'
              : 'flex-end',
          paddingHorizontal: Spacing.l,
          paddingVertical: Spacing.xl,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          backgroundColor: Colors.N0,
          paddingBottom:
            sizeClass === SizeClass.Small && Platform.OS !== 'web'
              ? Spacing.xxxl
              : Spacing.xl,
          ...(showBottomShadow &&
            !(Platform.OS !== 'web' && !groupMembersLessThanFifty) && {
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowOffset: { width: 0, height: -3 },
              shadowOpacity: 1,
              shadowRadius: 10,
              zIndex: 999,
            }),
        }}
      >
        {showBottomShadow && Platform.OS === 'android' ? (
          <LinearGradient
            colors={['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0.2)']}
            start={{ x: 1, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              position: 'absolute',
              top: -30,
              left: 0,
              right: 0,
              bottom: 30,
              height: 30,
              zIndex: 1,
            }}
          />
        ) : null}
        {attendanceMode === 'edit' || groupMembersLessThanFifty ? (
          <KitText black bold fontSize={20}>
            {t('groups:totalHeadCountLabel', { count: totalHeadCount })}
          </KitText>
        ) : null}
        {Platform.OS === 'web' || isLandscape ? (
          <View
            style={{
              flexDirection: 'row',
              marginTop: sizeClass === SizeClass.Small ? Spacing.xl : 0,
            }}
          >
            <KitButton
              secondary
              color={Colors.N100}
              title={t('common:buttonTitleCancel')}
              paddingLeft={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
              paddingRight={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
              style={{ marginRight: Spacing.m }}
              onPress={handleCancel}
            />
            <KitButton
              disabled={
                attendanceMode === 'create' ||
                hasHeadcountChanged ||
                hasNoteChanged
                  ? false
                  : !hasCheckedMembersChanged
              }
              color={Colors.N900}
              paddingLeft={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
              paddingRight={sizeClass === SizeClass.Small ? 60 : Spacing.xl}
              title={
                attendanceMode === 'edit'
                  ? t('common:buttonTitleSave')
                  : t('common:submitButtonTitle')
              }
              onPress={handleSave}
              testID={attendanceMode}
            />
          </View>
        ) : null}
      </View>
    </>
  );
};
const ScrollViewWrapper = (
  props: ScrollViewProps & {
    setScrollable?: (scrollable: boolean) => void;
  }
) => {
  const contentHeightRef = useRef(0);
  const layoutHeightRef = useRef(0);

  const onContentSizeChange = (width: number, height: number) => {
    contentHeightRef.current = height;
    checkScrollable();
  };

  const onLayout = (event: any) => {
    layoutHeightRef.current = event.nativeEvent.layout.height;
    checkScrollable();
  };

  const checkScrollable = () => {
    const isScrollable = contentHeightRef.current > layoutHeightRef.current;
    props.setScrollable?.(isScrollable);
  };

  return (
    <KeyboardAwareScrollView
      {...props}
      onContentSizeChange={onContentSizeChange}
      onLayout={onLayout}
      style={{ flex: 1, backgroundColor: Colors.N0 }}
      extraScrollHeight={
        Platform.OS === 'web' ? 0 : Platform.OS === 'android' ? 200 : 70
      }
      enableOnAndroid={true}
      enableAutomaticScroll={Platform.OS === 'android'}
      // avoid modal closing on scroll
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {props.children}
      <PlatformKeyboardSpacer />
    </KeyboardAwareScrollView>
  );
};

export default CreateOrUpdateGroupAttendance;
